/**
 * iOS-style interactive edge swipe for the PWA and non-iOS mobile browsers:
 * drag in from the left edge to go back, from the right edge to go forward.
 * The page follows the finger (back: the app slides right over a dimmed
 * underlay; forward: a blank page slides in over the app, which drifts left at
 * parallax speed) and commits past 35% of the width or on a flick, otherwise
 * springs back. With a sheet or the drawer open, the swipe closes it instead.
 *
 * Off in the iOS app (WKWebView's own swipe handles it), in iOS Safari tabs
 * (Safari's swipe would fire too), and for mouse/pen input. See lib/edge-swipe.ts.
 */
import { useEffect, useRef, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { isNative, haptic } from '../lib/native.js';
import { detectPlatform, isStandalone } from '../lib/viewport.js';
import { canGoBack, canGoForward } from '../lib/nav-history.js';
import { closeTopOverlay, hasOverlay } from '../lib/overlays.js';
import { NO_EDGE_SWIPE, edgeAt, edgeSwipeEnabled, scrollerWouldMove, shouldCommit, type Edge } from '../lib/edge-swipe.js';

const LOCK_PX = 8;
const PARALLAX = 0.3;
const SPRING_MS = 260;

interface Gesture {
  id: number;
  edge: Edge;
  x0: number;
  y0: number;
  width: number;
  overlay: boolean;
  state: 'pending' | 'dragging';
  travel: number;
  samples: Array<{ x: number; t: number }>;
}

export function EdgeSwipe({ appRef }: { appRef: RefObject<HTMLDivElement | null> }): ReactNode {
  const under = useRef<HTMLDivElement>(null);
  const ghost = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const enabled = (): boolean =>
      edgeSwipeEnabled({ native: isNative(), standalone: isStandalone(), platform: detectPlatform() });
    if (!enabled()) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    let g: Gesture | null = null;
    let busy = false; // commit/spring animation in flight

    const app = (): HTMLDivElement | null => appRef.current;

    const paint = (edge: Edge, travel: number, width: number, animate: boolean): void => {
      const a = app();
      const u = under.current;
      const gh = ghost.current;
      if (!a || !u || !gh) return;
      const p = Math.min(1, travel / width);
      const ease = animate ? `transform ${SPRING_MS}ms var(--ease-out), --dim ${SPRING_MS}ms var(--ease-out)` : 'none';
      a.style.transition = ease;
      u.style.transition = ease;
      gh.style.transition = ease;
      if (edge === 'left') {
        a.dataset.edgeSwipe = 'back';
        a.style.transform = `translate3d(${travel}px, 0, 0)`;
        u.hidden = false;
        u.style.setProperty('--dim', String(1 - p));
        gh.hidden = true;
      } else {
        a.dataset.edgeSwipe = 'forward';
        a.style.transform = `translate3d(${-travel * PARALLAX}px, 0, 0)`;
        u.hidden = true;
        gh.hidden = false;
        gh.style.opacity = '1';
        gh.style.transform = `translate3d(${width - travel}px, 0, 0)`;
        gh.style.setProperty('--dim', String(p));
      }
    };

    const reset = (): void => {
      const a = app();
      if (a) {
        a.style.transition = '';
        a.style.transform = '';
        delete a.dataset.edgeSwipe;
      }
      if (under.current) under.current.hidden = true;
      if (ghost.current) {
        ghost.current.hidden = true;
        ghost.current.style.transform = '';
      }
    };

    const after = (ms: number, fn: () => void): void => {
      window.setTimeout(fn, reduced.matches ? 0 : ms);
    };

    /** Resolves once the router has handled the popstate (or after a short timeout). */
    const go = (dir: 'back' | 'forward'): Promise<void> =>
      new Promise((resolve) => {
        let done = false;
        const finish = (): void => {
          if (done) return;
          done = true;
          window.removeEventListener('popstate', finish);
          // Let React render the destination before uncovering it.
          requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
        };
        window.addEventListener('popstate', finish);
        window.setTimeout(finish, 500);
        if (dir === 'back') window.history.back();
        else window.history.forward();
      });

    const commit = (edge: Edge, width: number): void => {
      busy = true;
      haptic('selection');
      if (reduced.matches) {
        reset();
        void go(edge === 'left' ? 'back' : 'forward').then(() => (busy = false));
        return;
      }
      paint(edge, width, width, true);
      after(SPRING_MS, () => {
        void go(edge === 'left' ? 'back' : 'forward').then(() => {
          const a = app();
          if (edge === 'left' && a) {
            // The previous page arrives from slightly left: the parallax half of the iOS push.
            a.style.transition = 'none';
            a.style.transform = `translate3d(${-width * PARALLAX}px, 0, 0)`;
            a.dataset.edgeSwipe = 'enter';
            if (under.current) under.current.hidden = true;
            void a.offsetWidth;
            a.style.transition = `transform ${SPRING_MS}ms var(--ease-out), opacity ${SPRING_MS}ms var(--ease-out)`;
            a.style.transform = 'translate3d(0, 0, 0)';
          } else if (a) {
            // Forward: the real page replaces the blank one sliding in; fade the blank away.
            a.style.transition = 'none';
            a.style.transform = '';
            if (ghost.current) {
              ghost.current.style.transition = `opacity 160ms var(--ease-out)`;
              ghost.current.style.opacity = '0';
            }
          }
          after(SPRING_MS, () => {
            reset();
            busy = false;
          });
        });
      });
    };

    const springBack = (edge: Edge, width: number): void => {
      busy = true;
      paint(edge, 0, width, !reduced.matches);
      after(SPRING_MS, () => {
        reset();
        busy = false;
      });
    };

    const onDown = (e: PointerEvent): void => {
      if (busy || g || e.pointerType !== 'touch' || !e.isPrimary) return;
      const width = window.innerWidth;
      const edge = edgeAt(e.clientX, width);
      if (!edge) return;
      const target = e.target instanceof Element ? e.target : null;
      if (target?.closest(NO_EDGE_SWIPE)) return;
      const overlay = hasOverlay();
      if (!overlay) {
        if (edge === 'left' ? !canGoBack() : !canGoForward()) return;
        if (scrollerWouldMove(target, edge)) return;
      }
      g = {
        id: e.pointerId,
        edge,
        x0: e.clientX,
        y0: e.clientY,
        width,
        overlay,
        state: 'pending',
        travel: 0,
        samples: [{ x: e.clientX, t: e.timeStamp }],
      };
    };

    const onMove = (e: PointerEvent): void => {
      if (!g || e.pointerId !== g.id) return;
      const dx = e.clientX - g.x0;
      const dy = e.clientY - g.y0;
      if (g.state === 'pending') {
        if (Math.abs(dx) < LOCK_PX && Math.abs(dy) < LOCK_PX) return;
        const inward = g.edge === 'left' ? dx > 0 : dx < 0;
        if (!inward || Math.abs(dy) > Math.abs(dx)) {
          g = null;
          return;
        }
        g.state = 'dragging';
      }
      g.travel = Math.max(0, g.edge === 'left' ? dx : -dx);
      g.samples.push({ x: e.clientX, t: e.timeStamp });
      while (g.samples.length > 2 && e.timeStamp - g.samples[0]!.t > 100) g.samples.shift();
      if (!g.overlay) paint(g.edge, g.travel, g.width, false);
    };

    const onUp = (e: PointerEvent): void => {
      if (!g || e.pointerId !== g.id) return;
      const cur = g;
      g = null;
      if (cur.state !== 'dragging') return;
      const first = cur.samples[0]!;
      const last = cur.samples[cur.samples.length - 1]!;
      const dt = Math.max(1, last.t - first.t);
      const speed = ((last.x - first.x) / dt) * (cur.edge === 'left' ? 1 : -1);
      const ok = shouldCommit(cur.travel, cur.width, speed);
      if (cur.overlay) {
        if (ok || cur.travel > 60) closeTopOverlay();
        return;
      }
      if (ok) commit(cur.edge, cur.width);
      else springBack(cur.edge, cur.width);
    };

    const onCancel = (e: PointerEvent): void => {
      if (!g || e.pointerId !== g.id) return;
      const cur = g;
      g = null;
      if (cur.state === 'dragging' && !cur.overlay) springBack(cur.edge, cur.width);
    };

    // Claim the touch once the drag is ours, so the page doesn't also scroll.
    const onTouchMove = (e: TouchEvent): void => {
      if (g?.state === 'dragging' && e.cancelable) e.preventDefault();
    };

    window.addEventListener('pointerdown', onDown, { capture: true, passive: true });
    window.addEventListener('pointermove', onMove, { capture: true, passive: true });
    window.addEventListener('pointerup', onUp, { capture: true, passive: true });
    window.addEventListener('pointercancel', onCancel, { capture: true, passive: true });
    window.addEventListener('touchmove', onTouchMove, { capture: true, passive: false });
    return () => {
      window.removeEventListener('pointerdown', onDown, { capture: true });
      window.removeEventListener('pointermove', onMove, { capture: true });
      window.removeEventListener('pointerup', onUp, { capture: true });
      window.removeEventListener('pointercancel', onCancel, { capture: true });
      window.removeEventListener('touchmove', onTouchMove, { capture: true });
      reset();
    };
  }, [appRef]);

  return createPortal(
    <>
      <div ref={under} className="edge-under" aria-hidden="true" hidden />
      <div ref={ghost} className="edge-ghost" aria-hidden="true" hidden />
    </>,
    document.body,
  );
}
